import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Checkbox,
  Alert,
  Divider,
  LinearProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import api from '../../../services/api';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * ServiceGroupTimesOfService Component
 *
 * Allows configuring Times of Service overrides for a specific service group.
 */
const ServiceGroupTimesOfService = ({ open, onClose, serviceGroup, onSave }) => {
  const [config, setConfig] = useState(null);
  const [platformConfig, setPlatformConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch current config
  useEffect(() => {
    if (open && serviceGroup) {
      fetchConfig();
    }
  }, [open, serviceGroup]);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch both platform config and service group config
      const [platformRes, groupRes] = await Promise.all([
        api.get('/admin/times-of-service'),
        api.get(`/admin/times-of-service/service-group/${serviceGroup._id}`)
      ]);

      if (platformRes.data.success) {
        setPlatformConfig(platformRes.data.config);
      }

      if (groupRes.data.success) {
        setConfig(groupRes.data.config || {
          overrideEnabled: false,
          weeklySchedule: null,
          gracePeriod: {},
          outOfHoursExperience: {
            mode: 'platform_default',
            additionalPromptContext: '',
            groupEmergencyContacts: []
          }
        });
      }
    } catch (err) {
      console.error('Error fetching ToS config:', err);
      setError('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const response = await api.put(
        `/admin/times-of-service/service-group/${serviceGroup._id}`,
        config
      );

      if (response.data.success) {
        setSuccess('Configuration saved successfully');
        if (onSave) onSave();
        setTimeout(() => {
          setSuccess(null);
        }, 3000);
      }
    } catch (err) {
      console.error('Error saving ToS config:', err);
      setError('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateSchedule = (dayIndex, field, value) => {
    const currentSchedule = config.weeklySchedule ||
      platformConfig?.weeklySchedule?.map(s => ({ ...s })) ||
      DAYS_OF_WEEK.map((_, i) => ({ dayOfWeek: i, isOpen: false, periods: [] }));

    const newSchedule = [...currentSchedule];
    if (!newSchedule[dayIndex]) {
      newSchedule[dayIndex] = { dayOfWeek: dayIndex, isOpen: false, periods: [] };
    }

    if (field === 'isOpen') {
      newSchedule[dayIndex] = { ...newSchedule[dayIndex], isOpen: value };
      if (value && (!newSchedule[dayIndex].periods || newSchedule[dayIndex].periods.length === 0)) {
        newSchedule[dayIndex].periods = [{ startTime: '09:00', endTime: '17:00' }];
      }
    } else if (field === 'startTime' || field === 'endTime') {
      newSchedule[dayIndex] = {
        ...newSchedule[dayIndex],
        periods: [{ ...newSchedule[dayIndex].periods?.[0], [field]: value }]
      };
    }

    updateConfig('weeklySchedule', newSchedule);
  };

  const applyPreset = (preset) => {
    let newSchedule;
    switch (preset) {
      case '24/7':
        newSchedule = DAYS_OF_WEEK.map((_, index) => ({
          dayOfWeek: index,
          isOpen: true,
          periods: [{ startTime: '00:00', endTime: '23:59' }]
        }));
        break;
      case 'business':
        newSchedule = DAYS_OF_WEEK.map((_, index) => ({
          dayOfWeek: index,
          isOpen: index >= 1 && index <= 5,
          periods: index >= 1 && index <= 5 ? [{ startTime: '09:00', endTime: '17:00' }] : []
        }));
        break;
      case 'copy_platform':
        newSchedule = platformConfig?.weeklySchedule?.map(s => ({ ...s })) || null;
        break;
      default:
        return;
    }
    updateConfig('weeklySchedule', newSchedule);
  };

  const addEmergencyContact = () => {
    const contacts = config.outOfHoursExperience?.groupEmergencyContacts || [];
    updateConfig('outOfHoursExperience', {
      ...config.outOfHoursExperience,
      groupEmergencyContacts: [...contacts, { name: '', phone: '', description: '' }]
    });
  };

  const updateEmergencyContact = (index, field, value) => {
    const contacts = [...(config.outOfHoursExperience?.groupEmergencyContacts || [])];
    contacts[index] = { ...contacts[index], [field]: value };
    updateConfig('outOfHoursExperience', {
      ...config.outOfHoursExperience,
      groupEmergencyContacts: contacts
    });
  };

  const removeEmergencyContact = (index) => {
    const contacts = [...(config.outOfHoursExperience?.groupEmergencyContacts || [])];
    contacts.splice(index, 1);
    updateConfig('outOfHoursExperience', {
      ...config.outOfHoursExperience,
      groupEmergencyContacts: contacts
    });
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ScheduleIcon />
        Service Hours Override: {serviceGroup?.name}
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ py: 4 }}>
            <LinearProgress />
            <Typography sx={{ mt: 2, textAlign: 'center' }}>Loading configuration...</Typography>
          </Box>
        ) : !platformConfig?.enabled ? (
          <Alert severity="info">
            Times of Service is not enabled at the platform level. Enable it in the main
            Times of Service settings first.
          </Alert>
        ) : (
          <Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            {/* Override Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={config?.overrideEnabled || false}
                  onChange={(e) => updateConfig('overrideEnabled', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle1">Enable Schedule Override</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Use a custom schedule for this service group instead of platform defaults
                  </Typography>
                </Box>
              }
            />

            {config?.overrideEnabled && (
              <>
                <Divider sx={{ my: 3 }} />

                {/* Quick Presets */}
                <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Quick Presets</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button variant="outlined" size="small" onClick={() => applyPreset('24/7')}>
                      24/7 Access
                    </Button>
                    <Button variant="outlined" size="small" onClick={() => applyPreset('business')}>
                      Business Hours
                    </Button>
                    <Button variant="outlined" size="small" onClick={() => applyPreset('copy_platform')}>
                      Copy Platform Schedule
                    </Button>
                  </Box>
                </Box>

                {/* Weekly Schedule */}
                <Typography variant="subtitle2" sx={{ mb: 2 }}>Weekly Schedule (UTC)</Typography>
                {DAYS_OF_WEEK.map((day, index) => {
                  const schedule = config.weeklySchedule || platformConfig?.weeklySchedule || [];
                  const daySchedule = schedule.find(s => s.dayOfWeek === index) || { isOpen: false, periods: [] };

                  return (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={daySchedule.isOpen}
                            onChange={(e) => updateSchedule(index, 'isOpen', e.target.checked)}
                          />
                        }
                        label={<Typography sx={{ width: 100 }}>{day}</Typography>}
                      />
                      {daySchedule.isOpen ? (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            type="time"
                            size="small"
                            value={daySchedule.periods?.[0]?.startTime || '09:00'}
                            onChange={(e) => updateSchedule(index, 'startTime', e.target.value)}
                            sx={{ width: 120 }}
                          />
                          <Typography>to</Typography>
                          <TextField
                            type="time"
                            size="small"
                            value={daySchedule.periods?.[0]?.endTime || '17:00'}
                            onChange={(e) => updateSchedule(index, 'endTime', e.target.value)}
                            sx={{ width: 120 }}
                          />
                        </Box>
                      ) : (
                        <Typography color="text.secondary">Closed</Typography>
                      )}
                    </Box>
                  );
                })}

                <Divider sx={{ my: 3 }} />

                {/* Out-of-Hours Experience */}
                <Typography variant="subtitle2" sx={{ mb: 2 }}>Out-of-Hours Experience</Typography>

                <FormControl component="fieldset" sx={{ mb: 2 }}>
                  <FormLabel component="legend">Mode</FormLabel>
                  <RadioGroup
                    value={config.outOfHoursExperience?.mode || 'platform_default'}
                    onChange={(e) => updateConfig('outOfHoursExperience', {
                      ...config.outOfHoursExperience,
                      mode: e.target.value
                    })}
                  >
                    <FormControlLabel value="platform_default" control={<Radio />} label="Use Platform Default" />
                    <FormControlLabel value="helper_persona" control={<Radio />} label="Helper Persona (Limited Support)" />
                    <FormControlLabel value="closed_screen" control={<Radio />} label="Closed Screen (No Chat)" />
                  </RadioGroup>
                </FormControl>

                {/* Additional Prompt Context */}
                <TextField
                  label="Additional Context for Helper Persona"
                  fullWidth
                  multiline
                  rows={3}
                  value={config.outOfHoursExperience?.additionalPromptContext || ''}
                  onChange={(e) => updateConfig('outOfHoursExperience', {
                    ...config.outOfHoursExperience,
                    additionalPromptContext: e.target.value
                  })}
                  placeholder="Add group-specific context, contact details, or instructions for the helper persona..."
                  helperText="This context will be injected into the helper persona prompt for this service group"
                  sx={{ mb: 3 }}
                />

                {/* Group Emergency Contacts */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">Group-Specific Emergency Contacts</Typography>
                    <Button size="small" startIcon={<AddIcon />} onClick={addEmergencyContact}>
                      Add Contact
                    </Button>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    These contacts will be shown in addition to platform-level emergency contacts
                  </Typography>

                  {(config.outOfHoursExperience?.groupEmergencyContacts || []).map((contact, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                      <TextField
                        size="small"
                        label="Name"
                        value={contact.name}
                        onChange={(e) => updateEmergencyContact(index, 'name', e.target.value)}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        size="small"
                        label="Phone"
                        value={contact.phone}
                        onChange={(e) => updateEmergencyContact(index, 'phone', e.target.value)}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        size="small"
                        label="Description"
                        value={contact.description || ''}
                        onChange={(e) => updateEmergencyContact(index, 'description', e.target.value)}
                        sx={{ flex: 1.5 }}
                      />
                      <IconButton size="small" onClick={() => removeEmergencyContact(index)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving || loading || !platformConfig?.enabled}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceGroupTimesOfService;
